import { createJSONStorage, persist } from 'zustand/middleware'

import { create } from 'zustand'

import apiClient, { getCsrfCookie } from '@/api/axios'

import { decryptData, encryptData } from '@/lib/crypto'
import { logger } from '@/lib/logger'
import { clearUserContext, setUserContext } from '@/lib/sentry'

import { UserSettings } from '@/features/preferences/types/preferences.types'

import type { User } from '@/types/user'

import { API_ENDPOINTS } from '../api/endpoints'

interface AuthState {
  user: User | null
  settings: UserSettings | null
  loading: boolean
  submittingData: boolean
  error: string | null

  // Actions
  setUser: (user: User | null) => void
  setSettings: (settings: UserSettings | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void

  // Auth operations
  login: (email: string, password: string) => Promise<User | null>
  logout: () => Promise<void>
  fetchUser: () => Promise<void>

  // User data operations
  updateProfile: (data: Partial<User>) => Promise<void>
  updateSettings: (settings: Partial<UserSettings>) => Promise<void>
  updatePassword: (current: string, newPassword: string) => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      settings: null,
      loading: false,
      submittingData: false,
      error: null,

      // Setters
      setUser: (user) => set({ user }),
      setSettings: (settings) => set({ settings }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),

      // Auth methods
      login: async (email, password) => {
        set({ loading: true, error: null })
        try {
          await getCsrfCookie()
          const res = await apiClient.post(API_ENDPOINTS.auth.login, { email, password })
          const { user, settings } = res.data.data
          set({ user, settings, loading: false })

          // Set Sentry user context for error tracking
          setUserContext({
            id: user.id,
            email: user.email,
            role: user.roles?.[0],
            branchId: user.branch_id,
          })

          // Fetch fresh data after successful login
          const [{ useBranchStore }, { useAppSettings }] = await Promise.all([
            import('@/stores/useBranchStore'),
            import('@/stores/useAppSettings'),
          ])

          // Fetch data but don't let failures break login flow
          try {
            await Promise.all([
              useBranchStore.getState().fetchBranches(),
              useAppSettings.getState().fetchSettings(true), // Fetch authenticated app settings
            ])
          } catch (error) {
            // Log but don't throw - login should succeed even if post-login data fetching fails
            logger.error('Failed to fetch post-login data', error)
          }

          return user
        } catch (error: any) {
          logger.error('Login failed', error)
          const message = error.response?.data?.errorMessage || 'Login failed'
          set({ error: message, loading: false })
          throw new Error(message)
        }
      },

      logout: async () => {
        set({ loading: true })
        try {
          await getCsrfCookie()
          await apiClient.post(API_ENDPOINTS.auth.logout)
        } catch (error: unknown) {
          logger.error('Logout failed', error)
        } finally {
          // Clear Sentry user context
          clearUserContext()
          set({ user: null, settings: null, loading: false })
        }
      },

      fetchUser: async () => {
        set({ loading: true, error: null })
        try {
          await getCsrfCookie()
          const res = await apiClient.get(API_ENDPOINTS.auth.user)
          const { user, settings } = res.data.data
          set({ user, settings, loading: false })

          // Set Sentry user context
          setUserContext({
            id: user.id,
            email: user.email,
            role: user.roles?.[0],
            branchId: user.branch_id,
          })

          // Fetch fresh app settings after user data is loaded
          const { useAppSettings } = await import('@/stores/useAppSettings')
          await useAppSettings.getState().fetchSettings(true)
        } catch (error: unknown) {
          logger.error('Failed to fetch auth user', error)
          // Clear user context on fetch failure
          clearUserContext()
          set({ user: null, settings: null, loading: false })
        }
      },

      // User data methods
      updateProfile: async (data) => {
        set({ submittingData: true, error: null })
        try {
          await getCsrfCookie()
          const res = await apiClient.post(API_ENDPOINTS.user.settings, data)
          set({ user: res.data.data.user, submittingData: false })
        } catch (error: any) {
          logger.error('Failed to update profile data', error)
          const message = error.response?.data?.message || 'Profile update failed'
          set({ error: message, submittingData: false })
          throw new Error(message)
        }
      },

      updateSettings: async (settings) => {
        set({ submittingData: true, error: null })
        try {
          await getCsrfCookie()
          const res = await apiClient.patch(API_ENDPOINTS.user.settings, { settings })
          set({ settings: res.data.data.settings, submittingData: false })
        } catch (error: any) {
          logger.error('Failed to update settings', error)
          const message = error.response?.data?.message || 'Settings update failed'
          set({ error: message, submittingData: false })
          throw new Error(message)
        }
      },

      updatePassword: async (current, newPassword) => {
        set({ submittingData: true, error: null })
        try {
          await getCsrfCookie()
          await apiClient.post(API_ENDPOINTS.auth.pwdUpdate, {
            current_password: current,
            password: newPassword,
            password_confirmation: newPassword,
          })
          set({ submittingData: false })
        } catch (error: any) {
          logger.error('Failed to update password', error)
          const message = error.response?.data?.message || 'Password update failed'
          set({ error: message, submittingData: false })
          throw new Error(message)
        }
      },
    }),
    {
      name: 'bbg-auth',
      storage: createJSONStorage(() => ({
        getItem: (name) => {
          const encrypted = localStorage.getItem(name)
          if (!encrypted) return null
          try {
            const decrypted = decryptData(encrypted)
            return decrypted
          } catch (error) {
            // Silently remove corrupted data
            localStorage.removeItem(name)
            return null
          }
        },
        setItem: (name, value) => {
          try {
            const encrypted = encryptData(value)
            localStorage.setItem(name, encrypted)
          } catch (error) {
            logger.error('Failed to encrypt auth data', error)
            // Fallback to unencrypted storage
            localStorage.setItem(name, value)
          }
        },
        removeItem: (name) => localStorage.removeItem(name),
      })),
      partialize: (state) => ({
        // Only persist minimal data to check auth state
        user: state.user
          ? {
              id: state.user.id,
              email: state.user.email,
              roles: state.user.roles,
              permissions: state.user.permissions,
              locale: state.user.locale,
              timezone: state.user.timezone,
              // Add only what's needed for initial routing/auth checks
            }
          : null,
        settings: null, // Don't persist settings, fetch fresh
      }),
    }
  )
)
